import { supabase } from './supabase';

// A tiny in-memory cache + in-flight de-duplicator for Supabase RPC calls.
//
// Why this exists: screens unmount/remount on every navigation (App.tsx renders
// them conditionally), so without this every visit refetches summaries, table
// pages and — most wastefully — the stable `*_filter_options` lists from
// scratch. This also collapses duplicate concurrent calls (e.g. React strict
// mode double-effects, or two components asking for the same data) into one
// network round-trip.
//
// Design notes:
//   * Keyed by RPC name + a stable stringify of its args.
//   * We cache the *promise*, so concurrent callers share one in-flight request.
//   * Failures are never cached — the entry is dropped so the next call retries.
//   * TTL is per-call: stable lookups (filter options) can cache for minutes,
//     report data for under a minute so a quick re-visit is instant but numbers
//     stay fresh. This is a soft cache, not a source of truth — `SyncStatus`
//     already surfaces real data freshness to the user.

type RpcResult<T> = { data: T | null; error: { message?: string; code?: string } | null };

type Entry = { at: number; ttl: number; promise: Promise<RpcResult<unknown>> };

const cache = new Map<string, Entry>();

function keyOf(name: string, args: unknown): string {
  return name + '|' + JSON.stringify(args ?? null);
}

export const RPC_TTL = {
  /** Filter dropdowns, sync status — change rarely. */
  stable: 5 * 60_000,
  /** Report summaries / table pages — fresh enough for a reporting tool. */
  report: 45_000,
} as const;

export function cachedRpc<T = unknown>(
  name: string,
  args?: Record<string, unknown>,
  ttlMs: number = RPC_TTL.report,
): Promise<RpcResult<T>> {
  const key = keyOf(name, args);
  const now = Date.now();

  const hit = cache.get(key);
  if (hit && now - hit.at < hit.ttl) {
    return hit.promise as Promise<RpcResult<T>>;
  }

  const promise = (supabase.rpc(name, args as never) as unknown as Promise<RpcResult<T>>)
    .then((res) => {
      // Don't let an error response poison the cache.
      if (res.error) cache.delete(key);
      return res;
    })
    .catch((err) => {
      cache.delete(key);
      throw err;
    });

  cache.set(key, { at: now, ttl: ttlMs, promise });
  return promise as Promise<RpcResult<T>>;
}

/** Drop cached entries. Pass an RPC name to clear just that one, or nothing to clear all. */
export function invalidateRpc(name?: string): void {
  if (!name) { cache.clear(); return; }
  for (const k of [...cache.keys()]) {
    if (k.startsWith(name + '|')) cache.delete(k);
  }
}
